//src/components/ImageCropModalContent.jsx

import { InboxOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import type { GetProp, UploadProps } from 'antd';
import { Space, Upload } from 'antd';
import { MessageStates } from 'containers/MessageBox';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import {
  PROFILE_RASTER_IMAGE_ACCEPT,
  validateRasterProfileImage,
} from 'utils/safe-upload';
import Cropper from './pic-cropper';
import { useImageCropContext } from './pic-image-crop-provider';
import { RotationSlider, ZoomSlider } from './pic-sliders';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const { Dragger } = Upload;

export const ImageCropModalContent = ({
  handleDone,
  handleClose,
  profilePicture,
  mode = 'edit',
}) => {
  const { setImage, image } = useImageCropContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = React.useState(false);

  const getBase64 = useCallback((img: FileType, callback: (url: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result as string));
    reader.readAsDataURL(img);
  }, []);

  const handleFile = useCallback(
    (file: FileType) => {
      if (!file) return false;

      const validation = validateRasterProfileImage(file);
      if (!validation.ok) {
        dispatch(
          turnMessageOn({
            title: t('common.unsupported-file-type-title'),
            description: validation.message,
            type: MessageStates.WARNING,
          })
        );
        return false;
      }

      setIsDragging(false);
      getBase64(file, (url) => {
        setImage(url);
      });
      return false;
    },
    [dispatch, getBase64, setImage, t]
  );

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    handleFile(file);
    return false; // Prevent upload
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const showDragOverlay = !image || isDragging;

  return (
    <div className="text-center relative">
      <Space direction="vertical" align="center" style={{ width: '100%' }}>
        <div 
          className="crop-container mb-4" 
          style={{ position: 'relative' }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        >
          <Cropper profilePicture={profilePicture} />
          <Dragger
            accept={PROFILE_RASTER_IMAGE_ACCEPT}
            multiple={false}
            showUploadList={false}
            beforeUpload={beforeUpload}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: '20px',
              border: showDragOverlay ? '2px dashed #d9d9d9' : 'none',
              borderRadius: '8px',
              backgroundColor: showDragOverlay ? (image ? 'rgba(250, 250, 250, 0.95)' : '#fafafa') : 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              opacity: showDragOverlay ? 1 : 0,
              pointerEvents: image && !isDragging ? 'none' : 'auto',
              transition: 'opacity 0.2s ease',
            }}
          >
            <p className="ant-upload-drag-icon" style={{ marginBottom: '8px', opacity: showDragOverlay ? 1 : 0 }}>
              <InboxOutlined style={{ fontSize: '48px', color: 'var(--color-text-muted, #5a6a6a)' }} />
            </p>
            <p className="ant-upload-text" style={{ margin: '8px 0', fontSize: '14px', opacity: showDragOverlay ? 1 : 0 }}>
              {image 
                ? t('common.upload-another-picture')
                : t('common.upload-drag-drop-prompt')
              }
            </p>
          </Dragger>
        </div>
        <Space style={{ width: '100%', columnGap: 12 }}>
          <ZoomSlider className="mb-4" />
          <RotationSlider className="mb-4" />
        </Space>

        <Space>
          <ScopeButton
            type="default"
            width="100px"
            height="40px"
            onClick={handleClose}
          >
            {t('common.cancel')}
          </ScopeButton>
          <ScopeButton
            type="primary"
            width="100px"
            height="40px"
            onClick={handleDone}
          >
            {t('common.save')}
          </ScopeButton>
        </Space>
      </Space>
    </div>
  );
};
export default ImageCropModalContent;

